/**
 * Oudh Al Anfar — Find Your Scent
 * ---------------------------------------------------------------------------
 * A guided flow whose answers are metafield filter values. Each step maps to a
 * Search & Discovery filter key, so the recommendation logic lives in Shopify's
 * own filtering rather than in a hardcoded lookup table — which is what makes
 * it scalable: adding a question is a Theme Editor block, not a code change.
 *
 * The result is fetched with the Section Rendering API, so the matched
 * products are rendered by Liquid with the same card component as the rest of
 * the store, and nothing about pricing, availability or badges is duplicated
 * in JavaScript.
 *
 * If a full answer set returns nothing, the least decisive filters are dropped
 * one at a time until something matches. The shopper is told which preference
 * was relaxed. A quiz that dead-ends is worse than one that compromises.
 * ---------------------------------------------------------------------------
 */

class OaaQuiz extends HTMLElement {
  connectedCallback() {
    this.steps = [...this.querySelectorAll('[data-quiz-step]')];
    this.progress = this.querySelector('[data-quiz-progress]');
    this.progressLabel = this.querySelector('[data-quiz-progress-label]');
    this.results = this.querySelector('[data-quiz-results]');
    this.resultsBody = this.querySelector('[data-quiz-results-body]');
    this.resultsNote = this.querySelector('[data-quiz-results-note]');
    this.backButton = this.querySelector('[data-quiz-back]');
    this.restartButton = this.querySelector('[data-quiz-restart]');

    this.collection = this.dataset.collection || 'all';
    this.resultsSectionId = this.dataset.resultsSection || 'oaa-quiz-results';
    this.relaxedTemplate = this.dataset.relaxedTemplate || '';

    if (!this.steps.length) return;

    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    this.backButton?.addEventListener('click', () => this.#go(this.index - 1));
    this.restartButton?.addEventListener('click', () => this.#restart());

    this.#go(0);
  }

  index = 0;

  /** Ordered answers: [{ key, value, label, weight }] */
  get answers() {
    return this.steps
      .map((step) => {
        const checked = step.querySelector('input:checked');
        if (!checked) return null;
        return {
          key: step.dataset.quizKey,
          value: checked.value,
          label: checked.dataset.label || checked.value,
          // Lower weight is dropped first when nothing matches.
          weight: Number(step.dataset.quizWeight || 5),
        };
      })
      .filter(Boolean);
  }

  #onChange = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    // Auto-advance keeps the flow under a minute. The last step waits for the
    // explicit submit so the shopper can review before committing.
    const step = event.target.closest('[data-quiz-step]');
    if (!step) return;
    const stepIndex = this.steps.indexOf(step);
    if (stepIndex < this.steps.length - 1) {
      window.setTimeout(() => this.#go(stepIndex + 1), 180);
    }
  };

  #onClick = (event) => {
    const submit = event.target.closest('[data-quiz-submit]');
    if (submit) {
      event.preventDefault();
      this.#submit();
    }

    const skip = event.target.closest('[data-quiz-skip]');
    if (skip) {
      event.preventDefault();
      this.#go(this.index + 1);
    }
  };

  #go(next) {
    if (next < 0 || next >= this.steps.length) return;
    this.index = next;

    this.steps.forEach((step, i) => {
      step.hidden = i !== next;
    });

    if (this.results) this.results.hidden = true;
    if (this.backButton) this.backButton.hidden = next === 0;

    const percent = ((next + 1) / this.steps.length) * 100;
    if (this.progress) this.progress.style.setProperty('--oaa-quiz-progress', `${percent}%`);
    if (this.progressLabel) {
      this.progressLabel.textContent = this.progressLabel.dataset.template
        .replace('__CURRENT__', String(next + 1))
        .replace('__TOTAL__', String(this.steps.length));
    }

    // Move focus to the new step's legend so a keyboard or screen-reader user
    // is told where they have landed.
    this.steps[next]?.querySelector('[tabindex="-1"]')?.focus();
  }

  #restart() {
    this.querySelectorAll('input:checked').forEach((input) => {
      input.checked = false;
    });
    this.#go(0);
  }

  async #submit() {
    const answers = this.answers;
    if (!answers.length) return;

    this.#setBusy(true);

    // Drop the least decisive filter each time nothing matches.
    const ordered = [...answers].sort((a, b) => a.weight - b.weight);
    let active = [...answers];
    let relaxed = [];

    for (let attempt = 0; attempt <= answers.length; attempt += 1) {
      const html = await this.#fetchResults(active);

      if (html && html.trim() && !html.includes('data-quiz-empty')) {
        this.#renderResults(html, relaxed);
        this.#setBusy(false);
        return;
      }

      const weakest = ordered.find((answer) => active.includes(answer));
      if (!weakest) break;
      active = active.filter((answer) => answer !== weakest);
      relaxed.push(weakest);
    }

    // Nothing at all: show the unfiltered collection rather than a dead end.
    const fallback = await this.#fetchResults([]);
    this.#renderResults(fallback || '', answers);
    this.#setBusy(false);
  }

  async #fetchResults(answers) {
    const params = new URLSearchParams();
    for (const answer of answers) {
      if (answer.key && answer.value) params.append(answer.key, answer.value);
    }
    params.set('section_id', this.resultsSectionId);

    try {
      const response = await fetch(`/collections/${this.collection}?${params.toString()}`, {
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) return null;
      return await response.text();
    } catch {
      return null;
    }
  }

  #renderResults(html, relaxed) {
    if (!this.results || !this.resultsBody) return;

    // The Section Rendering API returns the whole section wrapper; take the
    // inner content so the results drop straight into the panel.
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const content = parsed.querySelector('[data-quiz-results-content]');
    this.resultsBody.replaceChildren(...(content ? [...content.childNodes] : []));

    this.steps.forEach((step) => {
      step.hidden = true;
    });
    this.results.hidden = false;
    if (this.backButton) this.backButton.hidden = true;

    if (this.resultsNote) {
      if (relaxed.length && this.relaxedTemplate) {
        this.resultsNote.hidden = false;
        this.resultsNote.textContent = this.relaxedTemplate.replace(
          '__PREFERENCES__',
          relaxed.map((answer) => answer.label).join(', ')
        );
      } else {
        this.resultsNote.hidden = true;
      }
    }

    this.results.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  #setBusy(busy) {
    this.toggleAttribute('data-busy', busy);
    this.setAttribute('aria-busy', String(busy));
  }
}

if (!customElements.get('oaa-quiz')) {
  customElements.define('oaa-quiz', OaaQuiz);
}
